import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { Search, RefreshCw, Plus, X, Upload, Mail, Download } from 'lucide-react';
import { useMobile } from '../hooks/useMobile';

const token = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

const OUTCOME_REASONS = [
  { value: 'remortgaged_elsewhere', label: 'Remortgaged elsewhere' },
  { value: 'sold_property', label: 'Sold property' },
  { value: 'paid_off', label: 'Paid off' },
  { value: 'staying_with_lender', label: 'Staying with existing lender' },
  { value: 'client_unresponsive', label: 'Client unresponsive' },
  { value: 'other', label: 'Other' },
];

const CASE_STATUS_LABELS = {
  active: 'Active',
  review_due: 'Review due',
  expired_product: 'Expired product',
  application_in_progress: 'Application in progress',
  closed: 'Closed',
  other: 'Other',
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

function RenewalChip({ dateStr }) {
  if (!dateStr) return <span style={{ color: '#9ca3af', fontSize: 12.5 }}>No current product</span>;
  const days = daysUntil(dateStr);
  const label = new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  let bg = '#e5e7eb', fg = '#374151';
  if (days < 0) { bg = '#fee2e2'; fg = '#b91c1c'; }
  else if (days <= 90) { bg = '#fef3c7'; fg = '#92400e'; }
  else if (days <= 240) { bg = '#e0f2fe'; fg = '#075985'; }
  return (
    <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color: fg }}>
      {days < 0 ? 'Overdue — ' : ''}{label}
    </span>
  );
}

// 2 Sep 2026: makes it visible from the list itself whether a renewal-alert email has
// actually gone out for this client yet (previously only visible by opening the client
// detail modal's outreach timeline, one client at a time - Tim flagged the list view
// gave no indication at all). Silent (renders nothing) when the client has no current
// product to alert on - only says "No alert sent yet" when there is one due and
// nothing has fired.
function AlertChip({ lastAlert, hasRenewal }) {
  if (!lastAlert) {
    if (!hasRenewal) return null;
    return <span style={{ fontSize: 11, color: '#9ca3af' }}>No alert sent yet</span>;
  }
  const sentLabel = new Date(lastAlert.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return (
    <span style={{ fontSize: 11, color: '#0e7490', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <Mail size={11} /> {lastAlert.months_before_end}mo alert sent {sentLabel}
    </span>
  );
}

// 3 Sept 2026: flags a win-back opportunity right on the list row - previously the only
// place this showed at all was the 🎯 badge inside the client detail card, so from the
// list a win-back case (current item only tracks when a competitor's deal ends, not a
// real renewal of ours) looked identical to a real upcoming renewal until you opened it.
// Same amber styling/wording as that existing card badge, just smaller for a row.
function WinBackChip() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      🎯 Win-back
    </span>
  );
}

// 4 Sep 2026: flags that a client has a confirmed upcoming booking against this
// property - real gap found while scoping WhatsApp, that a client booking a call about
// their renewal looked no different from one who'd never responded at all. Only shown
// while the booking is still upcoming (next_booking_at is future-only from the API) -
// once the date passes with no outcome logged the row just falls back to its normal
// overdue styling, same "Needs your attention" adviser nudge as today (deliberately not
// a persistent chip - the underlying alert-suppression itself IS persistent, this is
// just the at-a-glance signal for "there's something booked coming up").
function MeetingBookedChip({ when }) {
  const label = new Date(when).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, color: '#075985', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      📅 Meeting booked {label}
    </span>
  );
}

const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalBox = { background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto' };
const input = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', marginBottom: 12 };
const label = { display: 'block', fontSize: 12.5, fontWeight: 600, color: '#4a5568', marginBottom: 4 };
const btnPrimary = { background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 16px', fontSize: 13.5, cursor: 'pointer' };

function Modal({ title, onClose, children }) {
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Director-facing KPI strip (18 Aug 2026) — first cut of the "review this page from a
// director's perspective" ask. Reuses GET /api/backbook/summary (has existed since
// 14 Aug for the Dashboard's module-off Back Book section, was never rendered here on
// the actual /backbook page itself). Cards are informational only for now — not yet
// wired to filter the list below; that's a natural next step once the segmented/grouped
// list view is built.
function kpiCard(label, value, bg, fg) {
  return (
    <div style={{ flex: '1 1 120px', background: bg, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: fg, lineHeight: 1.1 }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11.5, color: fg, opacity: 0.85, marginTop: 2, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function BackBookSummaryStrip({ summary, canSeeAll, onSelectAdviser }) {
  if (!summary) return null;
  const t = summary.totals || {};
  const byAdviser = summary.byAdviser || [];
  const outcomes = summary.recentOutcomes || [];
  const renewed = outcomes.find(o => o.outcome === 'renewed')?.count || 0;
  const lost = outcomes.find(o => o.outcome === 'lost')?.count || 0;
  const unassignedCount = byAdviser.filter(a => !a.adviser_id).reduce((n, a) => n + a.client_count, 0);

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: byAdviser.length ? 12 : 0 }}>
        {kpiCard('Active clients', t.active_clients, '#f1f5f9', '#0e1b2c')}
        {kpiCard('Overdue', t.overdue, '#fee2e2', '#b91c1c')}
        {kpiCard('Due ≤ 1 month', t.due_1mo, '#fed7aa', '#9a3412')}
        {kpiCard('Due ≤ 2 months', t.due_2mo, '#ffedd5', '#c2410c')}
        {kpiCard('Due ≤ 3 months', t.due_3mo, '#fef3c7', '#92400e')}
        {kpiCard('Due ≤ 6 months', t.due_6mo, '#e0f2fe', '#075985')}
        {kpiCard('Due ≤ 8 months', t.due_8mo, '#eef2ff', '#3730a3')}
        {t.win_back_opportunities > 0 && kpiCard('🎯 Win-back opportunities', t.win_back_opportunities, '#fffbeb', '#92400e')}
        {(renewed > 0 || lost > 0) && (
          <div style={{ flex: '1 1 160px', background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0e1b2c' }}>Last 90 days</div>
            <div style={{ fontSize: 11.5, color: '#065f46', marginTop: 4 }}>{renewed} renewed</div>
            <div style={{ fontSize: 11.5, color: '#991b1b' }}>{lost} lost</div>
          </div>
        )}
      </div>

      {canSeeAll && byAdviser.length > 0 && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', background: '#f8fafc', fontSize: 11.5, fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            By adviser {unassignedCount > 0 && <span style={{ color: '#92400e', textTransform: 'none', fontWeight: 600 }}> · {unassignedCount} unassigned / house</span>}
          </div>
          {byAdviser.map((a) => (
            <div key={a.adviser_id || 'unassigned'}
              onClick={() => onSelectAdviser && onSelectAdviser(a.adviser_id || 'unassigned')}
              style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', borderTop: '1px solid #f1f5f9', fontSize: 13, cursor: onSelectAdviser ? 'pointer' : 'default' }}>
              <span style={{ color: '#0e1b2c', fontWeight: a.adviser_id ? 400 : 600, fontStyle: a.adviser_id ? 'normal' : 'italic' }}>
                {a.adviser_name || 'Unassigned / house account'}
              </span>
              <span style={{ color: '#718096' }}>
                {a.client_count} client{a.client_count === 1 ? '' : 's'}
                {a.due_3mo > 0 && <span style={{ color: '#b91c1c', fontWeight: 600, marginLeft: 8 }}>{a.due_3mo} due ≤3mo</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BackBookPage() {
  const mobile = useMobile();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canSeeAll = user.brokerLevel === 'owner' || user.brokerLevel === 'manager' || user.role === 'platform_owner';

  const [clients, setClients] = useState([]);
  const [advisers, setAdvisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [adviserFilter, setAdviserFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [error, setError] = useState('');

  const [addClientOpen, setAddClientOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [detailClientId, setDetailClientId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAdviserId, setBulkAdviserId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkErr, setBulkErr] = useState('');
  const [dataFilter, setDataFilter] = useState(null); // null | 'no_email' | 'no_phone' | 'no_product' (2 Sep 2026)
  const [outcomeForProductId, setOutcomeForProductId] = useState(null); // 2 Sep 2026 - inline "Log outcome" from the Overdue row itself

  const loadSummary = useCallback(() => {
    fetch('/api/backbook/summary', { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSummary(d))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (adviserFilter) params.set('adviser_id', adviserFilter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`/api/backbook/clients?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json();
      setClients(Array.isArray(data.clients) ? data.clients : []);
    } catch (err) {
      console.error(err);
      setError('Failed to load Back Book');
    } finally {
      setLoading(false);
    }
  }, [search, adviserFilter, statusFilter]);

  useEffect(() => { load(); setDataFilter(null); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // 3 Sept 2026 — deep link from the director digest email ("Team due" / "Your clients"
  // rows link to /backbook?client=<id>). Opens that client's detail card directly on
  // load, same modal the row's own "view" click already uses. Only fires once on mount —
  // if the user closes it, re-clicking the same email link (fresh page load) reopens it.
  useEffect(() => {
    const clientId = new URLSearchParams(window.location.search).get('client');
    if (clientId) setDetailClientId(clientId);
  }, []);

  // 3 Sept 2026 — same deep-link pattern as ?client= above, for the "Back Book by
  // adviser" click-through from both this page's own summary strip and the
  // Dashboard's module-off Back Book summary (BrokerDashboard.js), which used to
  // navigate to a bare /backbook with no filter applied at all (Adam Green, Alegra:
  // clicking an adviser's name should land on just that adviser's clients).
  useEffect(() => {
    const adviserId = new URLSearchParams(window.location.search).get('adviser_id');
    if (adviserId) setAdviserFilter(adviserId);
  }, []);

  // 4 Sep 2026 — same deep-link pattern again, for the Dashboard's "+ Add Back Book
  // client" quick action (BrokerDashboard.js), which used to navigate to a bare
  // /backbook with no way to actually start adding a client from there (Adam Green,
  // Alegra: the button promises to add a client but just lands on the list).
  useEffect(() => {
    const action = new URLSearchParams(window.location.search).get('action');
    if (action === 'add') setAddClientOpen(true);
  }, []);

  useEffect(() => {
    if (!canSeeAll) return;
    fetch('/api/backbook/advisers', { headers: authHeaders() })
      .then(r => r.ok ? r.json() : { advisers: [] })
      .then(d => setAdvisers(d.advisers || []))
      .catch(() => {});
  }, [canSeeAll]);

  const refreshAll = () => { load(); loadSummary(); };

  // 2 Sep 2026: exports exactly what's currently filtered/visible (search/adviser/status),
  // same params the list view itself sends - "Export" always matches what you're looking
  // at, not silently the whole book regardless of filters.
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (adviserFilter) params.set('adviser_id', adviserFilter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`/api/backbook/export?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) { alert('Export failed - try again in a moment.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backbook-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const runBulkReassign = async () => {
    if (!selectedIds.size) return;
    setBulkSaving(true); setBulkErr('');
    try {
      const body = { client_ids: [...selectedIds], adviser_user_id: bulkAdviserId === 'unassigned' ? null : (bulkAdviserId || null) };
      const res = await fetch('/api/backbook/clients/bulk-reassign', { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setBulkErr(d.error || 'Failed to reassign'); return; }
      setSelectedIds(new Set());
      setBulkAdviserId('');
      refreshAll();
    } catch {
      setBulkErr('Failed to reassign');
    } finally {
      setBulkSaving(false);
    }
  };

  // Urgency buckets for the grouped list (18 Aug 2026) — exclusive bands so every
  // row lands in exactly one section (unlike the KPI strip above, which is
  // deliberately cumulative — "due within X months"). Same 3/6/8-month cut points as
  // the default alert schedule, so the grouping lines up with what actually triggers a
  // reminder email.
  //
  // 2 Sep 2026: `clients` below is now one row per active PROPERTY (see /clients
  // rebuild), each carrying its own next_renewal_date - so this bucketing code did
  // not need to change at all to become per-property; it was already grouping
  // whatever rows it was given by their own date. What changed is only that a
  // 2-property client can now land in two different buckets at once, correctly.
  const BUCKETS = [
    { key: 'overdue', label: 'Overdue', match: (d) => d !== null && d < 0 },
    { key: 'due3', label: 'Due within 3 months', match: (d) => d !== null && d >= 0 && d <= 90 },
    { key: 'due6', label: 'Due in 3–6 months', match: (d) => d !== null && d > 90 && d <= 180 },
    { key: 'due8', label: 'Due in 6–8 months', match: (d) => d !== null && d > 180 && d <= 240 },
    { key: 'later', label: 'Due beyond 8 months', match: (d) => d !== null && d > 240 },
    { key: 'none', label: 'No current product', match: (d) => d === null },
  ];

  // Data-health checks (2 Sep 2026) — things that quietly break the point of Back
  // Book rather than just being cosmetically incomplete: no email means the renewal-alert
  // cron silently skips that client forever (skippedNoEmail in its own log, invisible from
  // here until now); no phone means a broker chasing by phone — or WhatsApp/SMS later
  // — has nothing to call; no current product means the client is sitting in the book
  // with nothing being tracked at all (also visible in the 'No current product' bucket
  // below, surfaced here too so it isn't something you only find by scrolling to the
  // bottom of a long list).
  function uniqueByClient(rows) {
    const seen = new Set();
    return rows.filter(r => (seen.has(r.id) ? false : (seen.add(r.id), true)));
  }
  const noEmailClients = uniqueByClient(clients.filter(c => !c.email));
  const noPhoneClients = uniqueByClient(clients.filter(c => !c.phone));
  const noProductClients = clients.filter(c => !c.next_renewal_date);
  // 3 Sep 2026 — properties sitting in the import-dedupe review queue
  // (BACKBOOK-IMPORT-DEDUPE-SCOPE.md §3.5), same banner pattern as the data-health
  // checks above: short, genuinely-actionable list (tier 1/2 never reach this queue at
  // all, only a genuine tier-3 conflict flagged "review later" does).
  const pendingDuplicateClients = clients.filter(c => c.duplicate_review_status === 'pending');
  const DATA_ISSUES = [
    { key: 'no_email', label: 'have no email on file — renewal alerts will never reach them', list: noEmailClients },
    { key: 'no_phone', label: 'have no phone number on file', list: noPhoneClients },
    { key: 'no_product', label: 'have no current product logged — nothing is being tracked for them', list: noProductClients },
    { key: 'pending_duplicates', label: 'have a possible duplicate property flagged during import, not yet reviewed', list: pendingDuplicateClients },
  ].filter(i => i.list.length > 0);

  const grouped = dataFilter
    ? [{ key: 'data_filter', label: 'Filtered results', clients: DATA_ISSUES.find(i => i.key === dataFilter)?.list || [] }]
    : BUCKETS.map(b => ({
        ...b,
        clients: clients.filter(c => b.match(daysUntil(c.next_renewal_date))),
      })).filter(g => g.clients.length > 0);

  return (
    <div className="broker-dashboard">
      <Sidebar />
      <main className="main-content" style={{ maxWidth: 1100, ...(mobile ? {} : { padding: '28px 32px' }) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0e1b2c' }}>Back Book</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#718096' }}>
              Existing clients, their properties, and when each product needs renewing.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={refreshAll} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', cursor: 'pointer' }}>
              <RefreshCw size={15} />
            </button>
            <button onClick={() => setImportOpen(true)} style={{ ...btnGhost, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Upload size={15} /> Import CSV
            </button>
            <button onClick={exportCsv} disabled={exporting} style={{ ...btnGhost, display: 'flex', alignItems: 'center', gap: 6, opacity: exporting ? 0.6 : 1 }}>
              <Download size={15} /> {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
            <button onClick={() => setAddClientOpen(true)} style={{ ...btnPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={15} /> Add Client
            </button>
          </div>
        </div>

        <BackBookSummaryStrip summary={summary} canSeeAll={canSeeAll}
          onSelectAdviser={canSeeAll ? (id) => setAdviserFilter(id) : undefined} />

        {dataFilter ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: '#1d4ed8' }}>
              Showing {grouped[0]?.clients.length || 0} client{(grouped[0]?.clients.length || 0) === 1 ? '' : 's'} that {DATA_ISSUES.find(i => i.key === dataFilter)?.label}
            </span>
            <button onClick={() => setDataFilter(null)} style={{ ...btnGhost, padding: '4px 10px', fontSize: 12.5 }}>Clear filter</button>
          </div>
        ) : DATA_ISSUES.length > 0 && (
          <div style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
            {DATA_ISSUES.map(issue => (
              <div key={issue.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#92400e', padding: '3px 0' }}>
                <span>⚠️ {issue.list.length} client{issue.list.length === 1 ? '' : 's'} {issue.label}</span>
                <button onClick={() => setDataFilter(issue.key)} style={{ background: 'none', border: 'none', color: '#92400e', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' }}>View</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <div style={{ position: 'relative', maxWidth: 260, flex: '1 1 200px' }}>
            <Search size={14} style={{ color: '#9ca3af', position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              placeholder="Search name, email, or address"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...input, marginBottom: 0, padding: '9px 12px 9px 32px' }}
            />
          </div>
          {canSeeAll && (
            <select value={adviserFilter} onChange={(e) => setAdviserFilter(e.target.value)} style={{ ...input, marginBottom: 0, width: 'auto' }}>
              <option value="">All advisers</option>
              <option value="unassigned">Unassigned / Company</option>
              {advisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ ...input, marginBottom: 0, width: 'auto' }}>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {canSeeAll && selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>{selectedIds.size} selected</span>
            <select value={bulkAdviserId} onChange={(e) => setBulkAdviserId(e.target.value)} style={{ ...input, marginBottom: 0, width: 'auto' }}>
              <option value="">Reassign to…</option>
              <option value="unassigned">Unassigned / Company</option>
              {advisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <button style={{ ...btnPrimary, opacity: bulkAdviserId ? 1 : 0.5 }} disabled={!bulkAdviserId || bulkSaving} onClick={runBulkReassign}>
              {bulkSaving ? 'Reassigning…' : 'Reassign selected'}
            </button>
            <button style={btnGhost} onClick={() => { setSelectedIds(new Set()); setBulkErr(''); }}>Clear selection</button>
            {bulkErr && <span style={{ color: '#b91c1c', fontSize: 12.5 }}>{bulkErr}</span>}
          </div>
        )}

        {error && <div style={{ color: '#b91c1c', marginBottom: 12, fontSize: 13 }}>{error}</div>}

        {loading ? (
          <div style={{ color: '#718096', fontSize: 13.5 }}>Loading…</div>
        ) : clients.length === 0 ? (
          <div style={{ color: '#718096', fontSize: 13.5, padding: '40px 0', textAlign: 'center' }}>
            No clients yet. Add your first back-book client to get started.
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.key} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0e1b2c' }}>{group.label}</span>
                <span style={{ fontSize: 11.5, color: '#718096', background: '#f1f5f9', borderRadius: 999, padding: '1px 8px' }}>{group.clients.length}</span>
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                {group.clients.map((c) => (
                  // key is per-PROPERTY now, not per-client (2 Sep 2026) - a client with
                  // 2 active properties renders as 2 rows here, possibly in 2 different
                  // buckets. Falls back to c.id for the zero-active-property synthetic
                  // row (property_id is null there; at most one such row per client, so
                  // c.id alone is still unique).
                  <div
                    key={c.property_id || c.id}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
                      padding: '14px 16px', borderBottom: '1px solid #f1f5f9', background: '#fff',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1 }} onClick={() => setDetailClientId(c.id)}>
                      {canSeeAll && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={(e) => { e.stopPropagation(); toggleSelect(c.id); }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#0e1b2c', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {c.name}
                          {c.current_product_id && c.is_own_business === false && <WinBackChip />}
                          {c.next_booking_at && <MeetingBookedChip when={c.next_booking_at} />}
                        </div>
                        <div style={{ fontSize: 12.5, color: '#718096' }}>
                          {c.adviser_name || <span style={{ fontStyle: 'italic' }}>Unassigned / Company</span>}
                          {' · '}{c.property_count} propert{c.property_count === 1 ? 'y' : 'ies'}
                          {c.email ? ` · ${c.email}` : ''}
                        </div>
                        {c.property_count > 1 && (
                          // Only shown for multi-property clients (2 Sep 2026) - a
                          // single-property client's address is implicit and would just
                          // be noise; here it's the one thing that tells two rows for
                          // the same name apart.
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                            {c.address_line1 || 'No active property'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <RenewalChip dateStr={c.next_renewal_date} />
                      <AlertChip lastAlert={c.last_alert} hasRenewal={!!c.next_renewal_date} />
                      {c.current_product_id && daysUntil(c.next_renewal_date) < 0 && (
                        // 2 Sep 2026: every row is now unambiguously one property/product,
                        // so this no longer needs the old "only when exactly 1 overdue
                        // product" guess (single_overdue_product_id) - it's just this
                        // row's own product, gated to the Overdue bucket.
                        <button
                          onClick={(e) => { e.stopPropagation(); setOutcomeForProductId(c.current_product_id); }}
                          style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, padding: '3px 9px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
                        >
                          Log outcome
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {addClientOpen && (
          <AddClientModal
            advisers={advisers}
            canAssign={canSeeAll}
            onClose={() => setAddClientOpen(false)}
            onCreated={() => { setAddClientOpen(false); refreshAll(); }}
          />
        )}
        {detailClientId && (
          <ClientDetailModal
            clientId={detailClientId}
            advisers={advisers}
            canAssign={canSeeAll}
            onClose={() => setDetailClientId(null)}
            onChanged={refreshAll}
          />
        )}
        {importOpen && (
          <ImportCsvModal
            advisers={advisers}
            canAssign={canSeeAll}
            onClose={() => setImportOpen(false)}
            onSuccess={() => { setImportOpen(false); refreshAll(); }}
          />
        )}
        {outcomeForProductId && (
          <OutcomeModal
            productId={outcomeForProductId}
            onClose={() => setOutcomeForProductId(null)}
            onDone={() => { setOutcomeForProductId(null); refreshAll(); }}
          />
        )}
      </main>
    </div>
  );
}

function AddClientModal({ advisers, canAssign, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [adviserId, setAdviserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true);
    setErr('');
    try {
      const body = { name, phone, email };
      // adviserId: '' (not touched) -> omit, defaults to me. 'unassigned' -> explicit
      // null. A real id -> that adviser. (18 Aug 2026 — "Unassigned / Company" option.)
      if (canAssign && adviserId === 'unassigned') body.adviser_user_id = null;
      else if (canAssign && adviserId) body.adviser_user_id = adviserId;
      const res = await fetch('/api/backbook/clients', { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setErr(d.error || 'Failed to add client'); return; }
      onCreated();
    } catch {
      setErr('Failed to add client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add Back Book Client" onClose={onClose}>
      <label style={label}>Client name</label>
      <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" />
      <label style={label}>Phone</label>
      <input style={input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07700 900123" />
      <label style={label}>Email</label>
      <input style={input} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" />
      {canAssign && (
        <>
          <label style={label}>Adviser</label>
          <select style={input} value={adviserId} onChange={(e) => setAdviserId(e.target.value)}>
            <option value="">Me</option>
            <option value="unassigned">Unassigned / Company</option>
            {advisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </>
      )}
      {err && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancel</button>
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Add Client'}</button>
      </div>
    </Modal>
  );
}

function ClientDetailModal({ clientId, onClose, onChanged, advisers, canAssign }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingPropertyFor, setAddingPropertyFor] = useState(false);
  const [newAddress, setNewAddress] = useState('');
  const [addingProductFor, setAddingProductFor] = useState(null); // propertyId
  const [outcomeFor, setOutcomeFor] = useState(null); // productId
  const [reassigning, setReassigning] = useState(false);
  const [reassignErr, setReassignErr] = useState('');
  const [loggingContactFor, setLoggingContactFor] = useState(null); // propertyId
  // 3 Sep 2026 — inline-editable contact fields (BACKBOOK-IMPORT-DEDUPE-SCOPE.md §3.6:
  // import's "Update" needs to be able to apply client name/phone/email, not just
  // property fields — PATCH /clients/:id already supported this, this modal just never
  // had inputs for it before, only adviser reassignment was editable here).
  const [editingContact, setEditingContact] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [contactSaving, setContactSaving] = useState(false);
  const [contactErr, setContactErr] = useState('');
  // 3 Sep 2026 — resolving a property sitting in the import-dedupe "review later" queue
  // (§3.5) straight from its own card.
  const [resolvingPropertyId, setResolvingPropertyId] = useState(null);
  const [resolveErr, setResolveErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/backbook/clients/${clientId}`, { headers: authHeaders() });
      const d = await res.json();
      setData(d);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const addProperty = async () => {
    if (!newAddress.trim()) return;
    await fetch(`/api/backbook/clients/${clientId}/properties`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ address_line1: newAddress }),
    });
    setNewAddress('');
    setAddingPropertyFor(false);
    load();
    onChanged();
  };

  const archive = async () => {
    if (!data) return;
    const archiving = data.client.status !== 'archived';
    if (!window.confirm(archiving
      ? 'Archive this client? This stops all renewal alerts and contact — use when they should never be contacted again.'
      : 'Un-archive this client and resume normal alerts?')) return;
    await fetch(`/api/backbook/clients/${clientId}`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status: archiving ? 'archived' : 'active' }),
    });
    load();
    onChanged();
  };

  const reassign = async (adviserId) => {
    setReassignErr('');
    const body = { adviser_user_id: adviserId === 'unassigned' ? null : adviserId };
    const res = await fetch(`/api/backbook/clients/${clientId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body) });
    if (!res.ok) { const d = await res.json().catch(() => ({})); setReassignErr(d.error || 'Failed to reassign'); return; }
    setReassigning(false);
    load();
    onChanged();
  };

  const startEditingContact = () => {
    setEditName(data.client.name || '');
    setEditPhone(data.client.phone || '');
    setEditEmail(data.client.email || '');
    setContactErr('');
    setEditingContact(true);
  };

  const saveContact = async () => {
    if (!editName.trim()) { setContactErr('Name is required'); return; }
    setContactSaving(true); setContactErr('');
    try {
      const res = await fetch(`/api/backbook/clients/${clientId}`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ name: editName.trim(), phone: editPhone, email: editEmail }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setContactErr(d.error || 'Failed to save'); return; }
      setEditingContact(false);
      load();
      onChanged();
    } catch {
      setContactErr('Failed to save');
    } finally {
      setContactSaving(false);
    }
  };

  // §3.5 — 'keep_existing' drops this pending property (the original wins), 'update'
  // applies this pending row's own values onto the original then drops this one, 'keep_both'
  // just clears the flag and leaves both as genuinely separate properties.
  const resolveDuplicate = async (propertyId, decision) => {
    setResolvingPropertyId(propertyId); setResolveErr('');
    try {
      const res = await fetch(`/api/backbook/properties/${propertyId}/resolve-duplicate`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ decision }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setResolveErr(d.error || 'Failed to resolve'); return; }
      load();
      onChanged();
    } catch {
      setResolveErr('Failed to resolve');
    } finally {
      setResolvingPropertyId(null);
    }
  };

  return (
    <Modal title={loading ? 'Loading…' : data?.client?.name || 'Client'} onClose={onClose}>
      {loading || !data ? null : (
        <>
          <div style={{ fontSize: 12.5, color: '#718096', marginBottom: 16 }}>
            {editingContact ? (
              <div style={{ marginBottom: 8 }}>
                <label style={label}>Name</label>
                <input style={input} value={editName} onChange={(e) => setEditName(e.target.value)} />
                <label style={label}>Phone</label>
                <input style={input} value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                <label style={label}>Email</label>
                <input style={input} value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                {contactErr && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{contactErr}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btnGhost} onClick={() => setEditingContact(false)}>Cancel</button>
                  <button style={btnPrimary} onClick={saveContact} disabled={contactSaving}>{contactSaving ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            ) : (
              <>
                {data.client.phone || 'No phone'} · {data.client.email || 'No email'}
                <button onClick={startEditingContact} style={{ marginLeft: 8, fontSize: 11.5, background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>
                  Edit
                </button>
              </>
            )}
            {data.client.status === 'archived' && (
              <span style={{ marginLeft: 8, color: '#b91c1c', fontWeight: 600 }}>Archived</span>
            )}
            <div style={{ marginTop: 6 }}>
              Adviser: {reassigning ? (
                <select
                  autoFocus
                  defaultValue={data.client.adviser_user_id || 'unassigned'}
                  onChange={(e) => reassign(e.target.value)}
                  onBlur={() => setReassigning(false)}
                  style={{ fontSize: 12.5, padding: '2px 6px', borderRadius: 6, border: '1px solid #e2e8f0' }}
                >
                  <option value="unassigned">Unassigned / Company</option>
                  {advisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              ) : (
                <>
                  <strong style={{ color: '#0e1b2c', fontStyle: data.client.adviser_name ? 'normal' : 'italic' }}>
                    {data.client.adviser_name || 'Unassigned / Company'}
                  </strong>
                  {canAssign && (
                    <button onClick={() => setReassigning(true)} style={{ marginLeft: 8, fontSize: 11.5, background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>
                      Reassign
                    </button>
                  )}
                </>
              )}
              {reassignErr && <span style={{ color: '#b91c1c', marginLeft: 8 }}>{reassignErr}</span>}
            </div>
          </div>

          {data.properties.map((p) => (
            <div key={p.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{p.address_line1}</div>
              <div style={{ fontSize: 11.5, color: '#718096', marginBottom: 8 }}>
                {[p.property_type, p.occupancy].filter(Boolean).join(' · ')}
                {p.case_status && (
                  <span style={{ marginLeft: 6, background: '#f1f5f9', borderRadius: 999, padding: '1px 8px', fontWeight: 600 }}>
                    {CASE_STATUS_LABELS[p.case_status] || p.case_status}
                  </span>
                )}
                {(p.last_review_date || p.next_review_date) && (
                  <span style={{ display: 'block', marginTop: 3 }}>
                    {p.last_review_date && `Last review ${new Date(p.last_review_date).toLocaleDateString('en-GB')}`}
                    {p.last_review_date && p.next_review_date ? ' · ' : ''}
                    {p.next_review_date && `Next review ${new Date(p.next_review_date).toLocaleDateString('en-GB')}`}
                  </span>
                )}
                {p.notes && <span style={{ display: 'block', marginTop: 3, fontStyle: 'italic' }}>{p.notes}</span>}
              </div>
              {p.duplicate_review_status === 'pending' && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>
                    ⚠️ Possible duplicate — flagged during import, not yet reviewed
                  </div>
                  {p.possible_duplicate_of_address && (
                    <div style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 8 }}>
                      Possibly the same as: <strong>{p.possible_duplicate_of_address}</strong>, already in your Back Book
                    </div>
                  )}
                  {resolveErr && resolvingPropertyId === null && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 6 }}>{resolveErr}</div>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button style={{ ...btnGhost, padding: '6px 10px', fontSize: 12 }} onClick={() => resolveDuplicate(p.id, 'keep_existing')} disabled={resolvingPropertyId === p.id}>
                      Keep existing
                    </button>
                    <button style={{ ...btnGhost, padding: '6px 10px', fontSize: 12 }} onClick={() => resolveDuplicate(p.id, 'update')} disabled={resolvingPropertyId === p.id}>
                      {resolvingPropertyId === p.id ? 'Resolving…' : 'Update'}
                    </button>
                    <button style={{ ...btnGhost, padding: '6px 10px', fontSize: 12 }} onClick={() => resolveDuplicate(p.id, 'keep_both')} disabled={resolvingPropertyId === p.id}>
                      Keep both
                    </button>
                  </div>
                </div>
              )}
              {p.products.length === 0 && (
                <div style={{ fontSize: 12.5, color: '#9ca3af', marginBottom: 8 }}>No product on file</div>
              )}
              {p.products.map((prod) => (
                <div key={prod.id} style={{
                  fontSize: 12.5, marginBottom: 6,
                  color: !prod.is_own_business ? '#92400e' : prod.is_current ? '#0e1b2c' : '#9ca3af',
                  ...(!prod.is_own_business ? { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '4px 8px' } : {}),
                }}>
                  {!prod.is_own_business && <strong>🎯 Win-back opportunity — </strong>}
                  {prod.lender || (prod.is_own_business ? 'Unknown lender' : 'Unknown lender (gone elsewhere)')} {prod.product_type || ''} {prod.rate || ''} — ends{' '}
                  {new Date(prod.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {(prod.balance || prod.estimated_value || prod.ltv) && (
                    <span style={{ display: 'block', color: '#718096', marginTop: 2 }}>
                      {prod.balance != null && `Balance £${Number(prod.balance).toLocaleString()}`}
                      {prod.estimated_value != null && ` · Value £${Number(prod.estimated_value).toLocaleString()}`}
                      {prod.ltv != null && ` · LTV ${prod.ltv}%`}
                      {prod.monthly_payment != null && ` · £${Number(prod.monthly_payment).toLocaleString()}/mo`}
                    </span>
                  )}
                  {prod.is_current ? (
                    <button onClick={() => setOutcomeFor(prod.id)} style={{ marginLeft: 8, fontSize: 11.5, background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>
                      Log outcome
                    </button>
                  ) : (
                    <span style={{ marginLeft: 8, fontStyle: 'italic' }}>
                      {prod.outcome === 'renewed' ? '(renewed)' : prod.outcome === 'lost' ? `(lost — ${OUTCOME_REASONS.find(r => r.value === prod.outcome_reason)?.label || prod.outcome_reason})` : ''}
                    </span>
                  )}
                </div>
              ))}
              {addingProductFor === p.id ? (
                <AddProductInline propertyId={p.id} onDone={() => { setAddingProductFor(null); load(); onChanged(); }} onCancel={() => setAddingProductFor(null)} />
              ) : (
                <button onClick={() => setAddingProductFor(p.id)} style={{ fontSize: 12, background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                  + Add product
                </button>
              )}

              <ContactHistory property={p} />
              {loggingContactFor === p.id ? (
                <LogContactInline
                  propertyId={p.id}
                  onDone={() => { setLoggingContactFor(null); load(); }}
                  onCancel={() => setLoggingContactFor(null)}
                />
              ) : (
                <button onClick={() => setLoggingContactFor(p.id)} style={{ fontSize: 12, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontWeight: 600, marginTop: 6 }}>
                  + Log contact
                </button>
              )}
            </div>
          ))}

          {addingPropertyFor ? (
            <div style={{ marginBottom: 12 }}>
              <input style={input} placeholder="Property address" value={newAddress} onChange={(e) => setNewAddress(e.target.value)} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnGhost} onClick={() => setAddingPropertyFor(false)}>Cancel</button>
                <button style={btnPrimary} onClick={addProperty}>Add Property</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddingPropertyFor(true)} style={{ fontSize: 12.5, background: 'none', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', width: '100%', marginBottom: 12 }}>
              + Add property
            </button>
          )}

          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, textAlign: 'right' }}>
            <button style={btnGhost} onClick={archive}>
              {data.client.status === 'archived' ? 'Un-archive' : 'Archive (stop contact)'}
            </button>
          </div>

          {outcomeFor && (
            <OutcomeModal
              productId={outcomeFor}
              onClose={() => setOutcomeFor(null)}
              onDone={() => { setOutcomeFor(null); load(); onChanged(); }}
            />
          )}
        </>
      )}
    </Modal>
  );
}

const CONTACT_METHOD_LABELS = { phone: '📞 Phone', letter: '✉️ Letter', email: '📧 Email', meeting: '🤝 Meeting', other: 'Contact' };

// Merges real automatic alert sends (backbook_alert_log, via product.alerts) with
// manually-logged contact (property.manual_contacts) into one chronological timeline —
// 18 Aug 2026 director-review follow-up. The automatic side has existed since 12 Aug
// but was never shown anywhere; this is the first place it's visible.
function ContactHistory({ property }) {
  const autoEntries = (property.products || []).flatMap(prod =>
    (prod.alerts || []).map(a => ({
      kind: 'auto',
      date: a.sent_at,
      label: `${a.months_before_end}-month reminder sent${a.sent_to_email ? ` to ${a.sent_to_email}` : ''}`,
    }))
  );
  const manualEntries = (property.manual_contacts || []).map(mc => ({
    kind: 'manual',
    date: mc.contacted_at,
    label: `${CONTACT_METHOD_LABELS[mc.method] || 'Contact'}${mc.logged_by_name ? ` by ${mc.logged_by_name}` : ''}${mc.note ? ` — ${mc.note}` : ''}`,
  }));
  const entries = [...autoEntries, ...manualEntries].sort((a, b) => new Date(b.date) - new Date(a.date));
  if (!entries.length) return null;
  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
        Contact history
      </div>
      {entries.map((e, i) => (
        <div key={i} style={{ fontSize: 12, color: e.kind === 'auto' ? '#075985' : '#374151', marginBottom: 2 }}>
          {new Date(e.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} — {e.label}
        </div>
      ))}
    </div>
  );
}

function LogContactInline({ propertyId, onDone, onCancel }) {
  const [method, setMethod] = useState('phone');
  const [contactedAt, setContactedAt] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const res = await fetch(`/api/backbook/properties/${propertyId}/contacts`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ method, contacted_at: contactedAt, note }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setErr(d.error || 'Failed to log contact'); return; }
      onDone();
    } catch {
      setErr('Failed to log contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: 10, marginTop: 8 }}>
      <select style={input} value={method} onChange={(e) => setMethod(e.target.value)}>
        <option value="phone">Phone call</option>
        <option value="letter">Letter</option>
        <option value="email">Email (manual)</option>
        <option value="meeting">Meeting</option>
        <option value="other">Other</option>
      </select>
      <label style={label}>Date</label>
      <input style={input} type="date" value={contactedAt} onChange={(e) => setContactedAt(e.target.value)} />
      <label style={label}>Note (optional)</label>
      <textarea style={{ ...input, minHeight: 60 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Left voicemail, will try again Thursday" />
      {err && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={btnGhost} onClick={onCancel}>Cancel</button>
        <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Log Contact'}</button>
      </div>
    </div>
  );
}

function AddProductInline({ propertyId, onDone, onCancel }) {
  const [lender, setLender] = useState('');
  const [rate, setRate] = useState('');
  const [productType, setProductType] = useState('fixed');
  const [endDate, setEndDate] = useState('');
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!endDate) { setErr('End date is required'); return; }
    const res = await fetch(`/api/backbook/properties/${propertyId}/products`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ lender, rate, product_type: productType, end_date: endDate }),
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); setErr(d.error || 'Failed to add product'); return; }
    onDone();
  };

  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: 10, marginTop: 8 }}>
      <input style={input} placeholder="Lender (e.g. Halifax)" value={lender} onChange={(e) => setLender(e.target.value)} />
      <input style={input} placeholder="Rate (e.g. 4.29%)" value={rate} onChange={(e) => setRate(e.target.value)} />
      <select style={input} value={productType} onChange={(e) => setProductType(e.target.value)}>
        <option value="fixed">Fixed</option>
        <option value="tracker">Tracker</option>
        <option value="discount">Discount</option>
        <option value="variable">Variable</option>
      </select>
      <label style={label}>End date</label>
      <input style={input} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      {err && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={btnGhost} onClick={onCancel}>Cancel</button>
        <button style={btnPrimary} onClick={submit}>Save Product</button>
      </div>
    </div>
  );
}

function OutcomeModal({ productId, onClose, onDone }) {
  const [choice, setChoice] = useState(null); // 'renewed' | 'lost'
  const [lender, setLender] = useState('');
  const [rate, setRate] = useState('');
  const [productType, setProductType] = useState('fixed');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [trackWinBack, setTrackWinBack] = useState(false);
  const [competitorLender, setCompetitorLender] = useState('');
  const [competitorEndDate, setCompetitorEndDate] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setErr('');
    let body;
    if (choice === 'renewed') {
      if (!endDate) { setErr('New end date is required'); return; }
      body = { outcome: 'renewed', lender, rate, product_type: productType, end_date: endDate };
    } else {
      if (!reason) { setErr('Pick a reason'); return; }
      if (trackWinBack && !competitorEndDate) { setErr('Enter the date their new deal ends, or untick win-back tracking'); return; }
      body = { outcome: 'lost', outcome_reason: reason, outcome_note: note };
      if (trackWinBack) {
        body.competitor_lender = competitorLender || null;
        body.competitor_end_date = competitorEndDate;
      }
    }
    setSaving(true);
    try {
      const res = await fetch(`/api/backbook/products/${productId}/outcome`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setErr(d.error || 'Failed to save outcome'); return; }
      onDone();
    } catch {
      setErr('Failed to save outcome');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Log Renewal Outcome" onClose={onClose}>
      {!choice ? (
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={{ ...btnPrimary, flex: 1 }} onClick={() => setChoice('renewed')}>Signed up — Yes</button>
          <button style={{ ...btnGhost, flex: 1 }} onClick={() => setChoice('lost')}>Signed up — No</button>
        </div>
      ) : choice === 'renewed' ? (
        <>
          <label style={label}>New lender</label>
          <input style={input} value={lender} onChange={(e) => setLender(e.target.value)} />
          <label style={label}>New rate</label>
          <input style={input} value={rate} onChange={(e) => setRate(e.target.value)} />
          <label style={label}>Product type</label>
          <select style={input} value={productType} onChange={(e) => setProductType(e.target.value)}>
            <option value="fixed">Fixed</option>
            <option value="tracker">Tracker</option>
            <option value="discount">Discount</option>
            <option value="variable">Variable</option>
          </select>
          <label style={label}>New end date</label>
          <input style={input} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          {err && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button style={btnGhost} onClick={() => setChoice(null)}>Back</button>
            <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </>
      ) : (
        <>
          <label style={label}>Reason</label>
          <select style={input} value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Select a reason…</option>
            {OUTCOME_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <label style={label}>Note (optional)</label>
          <textarea style={{ ...input, minHeight: 70 }} value={note} onChange={(e) => setNote(e.target.value)} />

          <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={trackWinBack} onChange={(e) => setTrackWinBack(e.target.checked)} />
            We know when their new deal ends — track for win-back
          </label>
          {trackWinBack && (
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#718096', marginBottom: 8, lineHeight: 1.4 }}>
                This puts the case back on the renewal-due list around that date so it isn't
                forgotten — no automatic email goes to the client for it, since it isn't our product.
              </div>
              <label style={label}>Their new lender (optional)</label>
              <input style={input} value={competitorLender} onChange={(e) => setCompetitorLender(e.target.value)} placeholder="e.g. Nationwide" />
              <label style={label}>Their new deal ends</label>
              <input style={input} type="date" value={competitorEndDate} onChange={(e) => setCompetitorEndDate(e.target.value)} />
            </div>
          )}

          {err && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button style={btnGhost} onClick={() => setChoice(null)}>Back</button>
            <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// CSV import (18 Aug 2026, BACKBOOK-RENEWALS-SCOPE.md §7). Same upload -> AI preview ->
// review -> commit shape as BulkInviteModal.jsx's introducer import, mapped onto
// POST /api/backbook/import/preview + /commit. Nothing is written to the database
// until "Import" is pressed on the review screen.
const importOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const importModalBox = { background: '#fff', borderRadius: 12, padding: 22, width: '100%', maxWidth: 780, maxHeight: '90vh', overflowY: 'auto' };
const chip = (bg, color) => ({ background: bg, color, fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999, marginRight: 8, display: 'inline-block' });

// The "Update" side of a tier-3 possible-duplicate decision (BACKBOOK-IMPORT-DEDUPE-
// SCOPE.md §3.4) — pre-filled with the APP's current value for every field that
// disagrees, the CSV's differing value shown as a caption underneath. The broker edits
// and saves like any other edit; nothing gets auto-swapped blind. Applies directly
// through the existing property/client/product endpoints (the same ones the rest of the
// app already uses) rather than waiting for the import commit step — by the time this
// import is committed, tier 3 rows resolved this way are a no-op beyond refreshing the
// fingerprint, since the real write already happened here.
const DATE_FIELDS = new Set(['last_review_date', 'next_review_date', 'end_date']);
function TierThreeUpdateForm({ p, onApplied, onCancel }) {
  const propFields = ['address_line1', 'property_type', 'occupancy', 'case_status', 'last_review_date', 'next_review_date', 'notes'];
  const clientFields = ['name', 'phone', 'email'];
  const productFields = ['end_date', 'lender', 'rate', 'product_type'];
  const [vals, setVals] = useState(() => {
    const v = {};
    for (const f of propFields) if (p.diff?.[f]) v[f] = p.diff[f].app ?? '';
    for (const f of clientFields) if (p.clientDiff?.[f]) v['client_' + f] = p.clientDiff[f].app ?? '';
    for (const f of productFields) if (p.diff?.[f]) v['product_' + f] = p.diff[f].app ?? '';
    return v;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, val) => setVals(v => ({ ...v, [k]: val }));

  const apply = async () => {
    setSaving(true); setErr('');
    try {
      const propPatch = {};
      for (const f of propFields) if (p.diff?.[f] && vals[f] !== undefined) propPatch[f] = vals[f];
      if (Object.keys(propPatch).length) {
        const r = await fetch(`/api/backbook/properties/${p.matchedPropertyId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(propPatch) });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Failed to update property');
      }
      const clientPatch = {};
      for (const f of clientFields) if (p.clientDiff?.[f] && vals['client_' + f] !== undefined) clientPatch[f] = vals['client_' + f];
      if (Object.keys(clientPatch).length && p.matchedClientId) {
        const r = await fetch(`/api/backbook/clients/${p.matchedClientId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(clientPatch) });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Failed to update client');
      }
      if (p.diff?.end_date && vals.product_end_date) {
        const r = await fetch(`/api/backbook/properties/${p.matchedPropertyId}/products`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ lender: vals.product_lender, rate: vals.product_rate, product_type: vals.product_product_type, end_date: vals.product_end_date }),
        });
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Failed to update product');
      }
      onApplied();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const fieldRow = (fieldKey, valueKey, diffSource, label_) => diffSource?.[fieldKey] ? (
    <div key={valueKey} style={{ marginBottom: 8 }}>
      <label style={label}>{label_}</label>
      <input
        style={input}
        type={DATE_FIELDS.has(fieldKey) ? 'date' : 'text'}
        value={vals[valueKey] ?? ''}
        onChange={(e) => set(valueKey, e.target.value)}
      />
      <div style={{ fontSize: 11, color: '#92400e' }}>CSV says: {String(diffSource[fieldKey].csv ?? '—')}</div>
    </div>
  ) : null;

  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: 10, marginTop: 6 }}>
      {propFields.map(f => fieldRow(f, f, p.diff, f.replace(/_/g, ' ')))}
      {clientFields.map(f => fieldRow(f, 'client_' + f, p.clientDiff, `Client ${f}`))}
      {fieldRow('end_date', 'product_end_date', p.diff, 'Product end date')}
      {p.diff?.end_date && (
        <>
          <label style={label}>Lender</label>
          <input style={input} value={vals.product_lender ?? ''} onChange={(e) => set('product_lender', e.target.value)} />
          <label style={label}>Rate</label>
          <input style={input} value={vals.product_rate ?? ''} onChange={(e) => set('product_rate', e.target.value)} />
          <label style={label}>Product type</label>
          <input style={input} value={vals.product_product_type ?? ''} onChange={(e) => set('product_product_type', e.target.value)} />
        </>
      )}
      {err && <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button style={btnGhost} onClick={onCancel}>Cancel</button>
        <button style={btnPrimary} onClick={apply} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}

function ImportCsvModal({ advisers, canAssign, onClose, onSuccess }) {
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [groups, setGroups] = useState([]); // editable copy of preview.groups
  const [result, setResult] = useState(null);
  const fileRef = React.useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(String(ev.target.result || ''));
    reader.readAsText(f);
  };

  const runPreview = async () => {
    if (!csvText.trim()) { setError('Choose a CSV file first'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/backbook/import/preview', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ csv: csvText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to read that CSV');
      setPreview(data);
      // Every property carries its dedupe tier from the server (BACKBOOK-IMPORT-DEDUPE-
      // SCOPE.md): 'new' (no match), '1' (identical), '2' (safe to auto-apply, no click
      // needed), '3' (genuine conflict — needs decision + updateEditing drive the inline
      // four-option UI below).
      setGroups(data.groups.map(g => ({
        ...g,
        include: true,
        properties: g.properties.map(p => ({ ...p, include: true, decision: p.tier === '3' ? null : undefined, updateEditing: false })),
      })));
      setStep('preview');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const updateClient = (gi, field, value) => {
    setGroups(gs => gs.map((g, i) => i === gi ? { ...g, client: { ...g.client, [field]: value } } : g));
  };
  const updateProperty = (gi, pi, field, value) => {
    setGroups(gs => gs.map((g, i) => i !== gi ? g : {
      ...g, properties: g.properties.map((p, j) => j === pi ? { ...p, [field]: value } : p),
    }));
  };
  const toggleGroup = (gi) => setGroups(gs => gs.map((g, i) => i === gi ? { ...g, include: !g.include } : g));
  const toggleProperty = (gi, pi) => setGroups(gs => gs.map((g, i) => i !== gi ? g : {
    ...g, properties: g.properties.map((p, j) => j === pi ? { ...p, include: !p.include } : p),
  }));
  const setDecision = (gi, pi, decision) => setGroups(gs => gs.map((g, i) => i !== gi ? g : {
    ...g, properties: g.properties.map((p, j) => j === pi ? { ...p, decision, updateEditing: false } : p),
  }));
  const setUpdateEditing = (gi, pi, val) => setGroups(gs => gs.map((g, i) => i !== gi ? g : {
    ...g, properties: g.properties.map((p, j) => j === pi ? { ...p, updateEditing: val } : p),
  }));

  // A tier-3 property left undecided blocks import — the whole point of the four-option
  // prompt is that a genuine conflict never gets silently resolved one way or the other.
  const undecidedTier3 = groups.some(g => g.include && g.properties.some(p => p.include && p.tier === '3' && !p.decision));

  const runCommit = async () => {
    const payload = groups
      .filter(g => g.include)
      .map(g => ({ ...g, properties: g.properties.filter(p => p.include).map(({ updateEditing, ...p }) => p) }))
      .filter(g => g.properties.length > 0 || g.client.name);
    if (!payload.length) { setError('Nothing selected to import'); return; }
    if (undecidedTier3) { setError('Every possible duplicate needs a decision before importing'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/backbook/import/commit', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ groups: payload }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setResult(data);
      setStep('done');
      onSuccess();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const includedClients = groups.filter(g => g.include).length;
  const includedProperties = groups.filter(g => g.include).reduce((n, g) => n + g.properties.filter(p => p.include).length, 0);

  return (
    <div style={importOverlay} onClick={onClose}>
      <div style={importModalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Import Back Book from CSV</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}><X size={18} /></button>
        </div>

        {step === 'upload' && (
          <>
            <p style={{ fontSize: 13, color: '#718096', lineHeight: 1.5, margin: '8px 0 14px' }}>
              Upload a CSV export of your existing client/property/product book — column names
              don't need to match ours, an AI pass maps and normalises the data. Nothing is saved
              until you review and confirm on the next screen.
            </p>
            <div onClick={() => fileRef.current?.click()} style={{ border: '2px dashed #cbd5e1', borderRadius: 10, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', background: '#f8fafc' }}>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: 'none' }} />
              <div style={{ fontSize: 28 }}>📄</div>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{fileName || 'Click to choose a CSV file'}</div>
            </div>
            {error && <div style={{ color: '#b91c1c', fontSize: 12.5, marginTop: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button style={btnGhost} onClick={onClose}>Cancel</button>
              <button style={btnPrimary} onClick={runPreview} disabled={loading || !csvText.trim()}>
                {loading ? 'Reading with AI…' : 'Preview import'}
              </button>
            </div>
          </>
        )}

        {step === 'preview' && preview && (
          <>
            <div style={{ margin: '10px 0 14px' }}>
              <span style={chip('#eff6ff', '#1d4ed8')}>{preview.summary.totalRows} row{preview.summary.totalRows === 1 ? '' : 's'}</span>
              <span style={chip('#d1fae5', '#065f46')}>{preview.summary.totalClients} client{preview.summary.totalClients === 1 ? '' : 's'}</span>
              <span style={chip('#e0f2fe', '#075985')}>{preview.summary.totalProperties} propert{preview.summary.totalProperties === 1 ? 'y' : 'ies'}</span>
              {preview.summary.lowConfidenceRows > 0 && <span style={chip('#fef3c7', '#92400e')}>{preview.summary.lowConfidenceRows} low-confidence</span>}
              {preview.summary.tierCounts?.unchanged > 0 && <span style={chip('#e5e7eb', '#374151')}>{preview.summary.tierCounts.unchanged} unchanged</span>}
              {preview.summary.tierCounts?.autoUpdate > 0 && <span style={chip('#dbeafe', '#1d4ed8')}>{preview.summary.tierCounts.autoUpdate} will auto-update</span>}
              {preview.summary.tierCounts?.needsReview > 0 && <span style={chip('#fee2e2', '#991b1b')}>{preview.summary.tierCounts.needsReview} need{preview.summary.tierCounts.needsReview === 1 ? 's' : ''} your review</span>}
            </div>
            {preview.unmatchedAdviserNames?.length > 0 && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: '#92400e', marginBottom: 12 }}>
                Couldn't match these adviser names to anyone in your team — left unassigned (defaults to you): {preview.unmatchedAdviserNames.join(', ')}
              </div>
            )}
            <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, padding: 4 }}>
              {groups.map((g, gi) => (
                <div key={g.key} style={{ borderBottom: '1px solid #f1f5f9', padding: '10px 8px', opacity: g.include ? 1 : 0.45 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <input type="checkbox" checked={g.include} onChange={() => toggleGroup(gi)} />
                    <input style={{ ...input, marginBottom: 0, width: 150 }} value={g.client.name || ''} onChange={(e) => updateClient(gi, 'name', e.target.value)} placeholder="Client name" />
                    <input style={{ ...input, marginBottom: 0, width: 120 }} value={g.client.phone || ''} onChange={(e) => updateClient(gi, 'phone', e.target.value)} placeholder="Phone" />
                    <input style={{ ...input, marginBottom: 0, width: 170 }} value={g.client.email || ''} onChange={(e) => updateClient(gi, 'email', e.target.value)} placeholder="Email" />
                    {canAssign && (
                      <select style={{ ...input, marginBottom: 0, width: 140 }} value={g.client.adviser_user_id || ''} onChange={(e) => updateClient(gi, 'adviser_user_id', e.target.value || null)}>
                        <option value="">Unassigned / Company</option>
                        {advisers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    )}
                  </div>

                  {/* tier 'new' — full editable row, same as before this feature. tier 1/2
                      — compact, no per-row decision (§4 build plan: "just counted"). */}
                  {g.properties.filter(p => p.tier !== '3').map((p) => {
                    const pi = g.properties.indexOf(p);
                    if (p.tier === 'new') {
                      return (
                        <div key={p.source_row} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginLeft: 24, marginBottom: 4, opacity: p.include ? 1 : 0.4 }}>
                          <input type="checkbox" checked={p.include} onChange={() => toggleProperty(gi, pi)} />
                          <input style={{ ...input, marginBottom: 0, width: 220 }} value={p.address_line1 || ''} onChange={(e) => updateProperty(gi, pi, 'address_line1', e.target.value)} placeholder="Address" />
                          <span style={{ fontSize: 12, color: '#374151' }}>{p.product.lender || '—'} {p.product.product_type || ''} {p.product.rate || ''}</span>
                          <input type="date" style={{ ...input, marginBottom: 0, width: 130 }}
                            value={p.product.end_date || ''}
                            onChange={(e) => updateProperty(gi, pi, 'product', { ...p.product, end_date: e.target.value })} />
                          <span style={chip('#d1fae5', '#065f46')}>new</span>
                          {p.confidence === 'low' && <span style={chip('#fef3c7', '#92400e')}>low confidence</span>}
                          {p.warnings?.length > 0 && (
                            <span style={{ fontSize: 11, color: '#92400e' }} title={p.warnings.join('; ')}>⚠ {p.warnings[0]}</span>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div key={p.source_row} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginLeft: 24, marginBottom: 4, opacity: p.include ? 1 : 0.4 }}>
                        <input type="checkbox" checked={p.include} onChange={() => toggleProperty(gi, pi)} />
                        <span style={{ fontSize: 12.5, color: '#374151' }}>{p.address_line1}</span>
                        {p.tier === '1'
                          ? <span style={chip('#e5e7eb', '#374151')}>unchanged</span>
                          : <span style={chip('#dbeafe', '#1d4ed8')}>will auto-update</span>}
                      </div>
                    );
                  })}

                  {/* tier 3 — a genuine conflict: the field-by-field diff + the four-option
                      decision, per BACKBOOK-IMPORT-DEDUPE-SCOPE.md §3. */}
                  {g.properties.filter(p => p.tier === '3').map((p) => {
                    const pi = g.properties.indexOf(p);
                    const allDiffs = [...Object.entries(p.diff || {}), ...Object.entries(p.clientDiff || {})];
                    return (
                      <div key={p.source_row} style={{ marginLeft: 24, marginBottom: 8, border: '1px solid #fecaca', background: '#fff7f7', borderRadius: 8, padding: 10 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>
                          We found a possible duplicate of {p.address_line1} — but the records don't match.
                        </div>
                        <div style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 8 }}>
                          {allDiffs.map(([f, v]) => (
                            <div key={f}>{f.replace(/_/g, ' ')}: App has "{v.app ?? '—'}" · CSV has "{v.csv ?? '—'}"</div>
                          ))}
                        </div>
                        {p.decision && !p.updateEditing ? (
                          <div style={{ fontSize: 12, color: '#065f46', fontWeight: 600 }}>
                            ✓ {{ keep_existing: 'Keeping existing', update: 'Updated', keep_both: 'Keeping both', review_later: 'Flagged to review later' }[p.decision]}
                            {' '}<button onClick={() => setDecision(gi, pi, null)} style={{ marginLeft: 6, fontSize: 11, background: 'none', border: 'none', color: '#065f46', textDecoration: 'underline', cursor: 'pointer' }}>change</button>
                          </div>
                        ) : p.updateEditing ? (
                          <TierThreeUpdateForm
                            p={p}
                            onApplied={() => setDecision(gi, pi, 'update')}
                            onCancel={() => setUpdateEditing(gi, pi, false)}
                          />
                        ) : (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }} onClick={() => setDecision(gi, pi, 'keep_existing')}>Keep existing</button>
                            <button style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }} onClick={() => setUpdateEditing(gi, pi, true)}>Update</button>
                            <button style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }} onClick={() => setDecision(gi, pi, 'keep_both')}>Keep both</button>
                            <button style={{ ...btnGhost, padding: '5px 10px', fontSize: 12 }} onClick={() => setDecision(gi, pi, 'review_later')}>Review later</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#718096', margin: '10px 0' }}>
              {includedClients} client{includedClients === 1 ? '' : 's'}, {includedProperties} propert{includedProperties === 1 ? 'y' : 'ies'} selected to import.
              {preview.summary.aiCallsSkippedByHash > 0 && ` ${preview.summary.aiCallsSkippedByHash} row${preview.summary.aiCallsSkippedByHash === 1 ? '' : 's'} recognised as an exact repeat — skipped re-reading with AI.`}
            </div>
            {undecidedTier3 && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 8 }}>Every possible duplicate above needs a decision before you can import.</div>}
            {error && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button style={btnGhost} onClick={() => { setStep('upload'); setPreview(null); setGroups([]); setError(''); }}>← Back</button>
              <button style={btnPrimary} onClick={runCommit} disabled={loading || includedClients === 0 || undecidedTier3}>
                {loading ? 'Importing…' : `Import ${includedClients} client${includedClients === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}

        {step === 'done' && result && (
          <>
            <div style={{ textAlign: 'center', padding: '18px 0 6px' }}>
              <div style={{ fontSize: 40 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginTop: 6 }}>
                Import complete
              </div>
              <div style={{ fontSize: 13, color: '#718096', marginTop: 4 }}>
                {result.clientsCreated} new client{result.clientsCreated === 1 ? '' : 's'}, {result.propertiesCreated} new propert{result.propertiesCreated === 1 ? 'y' : 'ies'}, {result.productsCreated} new product{result.productsCreated === 1 ? '' : 's'}
                {result.propertiesUpdated > 0 && `, ${result.propertiesUpdated} updated automatically`}
              </div>
              {result.flaggedForReview > 0 && (
                <div style={{ fontSize: 12.5, color: '#92400e', marginTop: 6 }}>{result.flaggedForReview} flagged for review later</div>
              )}
              {result.skipped?.length > 0 && (
                <div style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 6 }}>{result.skipped.length} skipped (no name)</div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button style={btnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
